import { LitElement, html, css } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { api } from '../../api';
import { store } from '../../store';
import { showToast } from '../../components/ui/toast';
import { icon } from '../../components/ui/icons';
import { t } from '../../i18n';

interface Folder { id: string; name: string; sort_order: number; }
interface Note { id: string; folder_id: string | null; title: string; content: string | null; author_name: string | null; updated_at: string; }

// Plain-text/Markdown files we can read straight into the note content field.
// NICE TO HAVE: import .docx (parse via mammoth or a Worker-side conversion) — not yet supported.
const TEXT_IMPORT_EXTS = ['.md', '.markdown', '.mdown', '.txt'];
const MAX_IMPORT_BYTES = 1024 * 1024; // 1 MB of text is plenty for a note

/**
 * <cadence-event-notes> — the Notes tab for an event. Markdown notes optionally grouped
 * into one level of folders, with a visual editor (<cadence-rich-text-editor>, edits
 * visually and stores Markdown) and read view (<cadence-markdown-renderer>). Read for
 * everyone with events.view;
 * editing gated by events.manage_notes.
 */
@customElement('cadence-event-notes')
export class CadenceEventNotes extends LitElement {
  @property() eventId = '';
  @state() private folders: Folder[] = [];
  @state() private notes: Note[] = [];
  @state() private loading = true;
  @state() private activeFolder = 'all'; // 'all' | folderId | 'unfiled'
  @state() private openId: string | null = null;

  // Editor modal state
  @state() private editing = false;
  @state() private editId: string | null = null;
  @state() private form: { title: string; content: string; folder_id: string } = { title: '', content: '', folder_id: '' };
  @state() private saving = false;
  @query('input[type=file]') private importInput!: HTMLInputElement;

  // New-folder modal state
  @state() private folderModalOpen = false;
  @state() private newFolderName = '';
  @state() private folderSaving = false;
  // When the folder modal was opened from inside the note editor, assign the new folder
  // to the note being edited once it's created.
  private folderAssignToNote = false;

  // Styled confirm dialog state (replaces window.confirm for destructive actions)
  @state() private confirmOpen = false;
  @state() private confirmBusy = false;
  @state() private confirmConfig: { heading: string; message: string; confirmLabel: string } | null = null;
  private confirmAction: (() => Promise<void>) | null = null;

  private readonly canManage = store.hasPermission('events.manage_notes');

  static styles = css`
    :host { display: block; }
    .bar { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: var(--space-3); }
    .chips { display: flex; gap: 6px; flex-wrap: wrap; flex: 1; }
    .chip { display: inline-flex; align-items: center; gap: 6px; padding: 4px 12px; border-radius: 999px; border: 1px solid var(--color-border); background: var(--color-bg-primary); color: var(--color-text-secondary); cursor: pointer; font-size: 0.82rem; }
    .chip.active { background: var(--color-primary); color: #fff; border-color: var(--color-primary); }
    .chip .x { opacity: 0.7; font-weight: 700; }
    .chip .x:hover { opacity: 1; }
    .note { border: 1px solid var(--color-border); border-radius: var(--radius-md); margin-bottom: 10px; background: var(--color-bg-primary); }
    .note > summary { cursor: pointer; padding: 12px 14px; list-style: none; display: flex; align-items: center; justify-content: space-between; gap: 10px; }
    .note > summary::-webkit-details-marker { display: none; }
    .note .title { font-weight: 600; flex: 1; min-width: 0; }
    .note .meta { color: var(--color-text-secondary); font-size: 0.78rem; white-space: nowrap; }
    .row-acts { display: inline-flex; align-items: center; gap: 2px; flex-shrink: 0; }
    .iconbtn { background: none; border: none; cursor: pointer; color: var(--color-text-secondary); padding: 5px; border-radius: 6px; display: inline-flex; align-items: center; }
    .iconbtn svg { width: 16px; height: 16px; }
    .iconbtn:hover { background: var(--color-bg-secondary); color: var(--color-text-primary); }
    .iconbtn.del:hover { color: var(--color-danger); }
    .note .body { padding: 0 14px 14px; border-top: 1px solid var(--color-border); }
    .efield { margin-bottom: var(--space-3); }
    .efield label { display: block; font-size: 0.82rem; color: var(--color-text-secondary); margin-bottom: 4px; }
    .efield-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 4px; }
    .efield-head label { margin-bottom: 0; }
    .import-btn { display: inline-flex; align-items: center; gap: 6px; background: none; border: 1px solid var(--color-border); border-radius: var(--radius-sm); padding: 3px 10px; font-size: 0.78rem; color: var(--color-text-secondary); cursor: pointer; }
    .import-btn:hover { border-color: var(--color-primary); color: var(--color-primary); }
    input[type=file] { display: none; }
  `;

  connectedCallback(): void { super.connectedCallback(); if (this.eventId) this.load(); }
  updated(c: Map<string, unknown>) { if (c.has('eventId') && this.eventId) this.load(); }

  private async load() {
    this.loading = true;
    try {
      const res = await api.get<{ folders: Folder[]; notes: Note[] }>(`/events/${this.eventId}/notes`);
      this.folders = res.folders; this.notes = res.notes;
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.loading = false; }
  }

  private get visibleNotes(): Note[] {
    if (this.activeFolder === 'all') return this.notes;
    if (this.activeFolder === 'unfiled') return this.notes.filter((n) => !n.folder_id);
    return this.notes.filter((n) => n.folder_id === this.activeFolder);
  }

  // ---- Folders ----
  /** Open the new-folder modal. `assignToNote` selects the new folder for the note being
   *  edited (when opened from inside the note editor). */
  private openFolderModal(assignToNote: boolean) {
    this.folderAssignToNote = assignToNote;
    this.newFolderName = '';
    this.folderModalOpen = true;
  }
  private async saveFolder() {
    const name = this.newFolderName.trim();
    if (!name) { showToast(t('events.toast.folderNameRequired'), 'error'); return; }
    this.folderSaving = true;
    try {
      const folder = await api.post<Folder>(`/events/${this.eventId}/note-folders`, { name });
      await this.load();
      if (this.folderAssignToNote) this.form = { ...this.form, folder_id: folder.id };
      this.folderModalOpen = false;
      showToast(t('events.toast.folderCreated'), 'success');
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.folderSaving = false; }
  }
  private deleteFolder(f: Folder) {
    this.askConfirm({
      heading: t('events.notes.deleteFolder.heading'),
      message: t('events.notes.deleteFolder.message', { name: f.name }),
      confirmLabel: t('events.notes.deleteFolder.confirm'),
    }, async () => {
      await api.del(`/events/${this.eventId}/note-folders/${f.id}`);
      if (this.activeFolder === f.id) this.activeFolder = 'all';
      await this.load();
    });
  }

  // ---- Confirm dialog ----
  private askConfirm(config: { heading: string; message: string; confirmLabel: string }, action: () => Promise<void>) {
    this.confirmConfig = config;
    this.confirmAction = action;
    this.confirmOpen = true;
  }
  private closeConfirm() {
    if (this.confirmBusy) return;
    this.confirmOpen = false;
    this.confirmAction = null;
  }
  private async runConfirm() {
    if (!this.confirmAction) return;
    this.confirmBusy = true;
    try {
      await this.confirmAction();
      this.confirmOpen = false;
      this.confirmAction = null;
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.confirmBusy = false; }
  }

  // ---- Notes ----
  private openNew() {
    this.editId = null;
    this.form = { title: '', content: '', folder_id: this.activeFolder !== 'all' && this.activeFolder !== 'unfiled' ? this.activeFolder : '' };
    this.editing = true;
  }
  private openEdit(n: Note) {
    this.editId = n.id;
    this.form = { title: n.title, content: n.content ?? '', folder_id: n.folder_id ?? '' };
    this.editing = true;
  }
  private async saveNote() {
    if (!this.form.title.trim()) { showToast(t('events.toast.titleRequired'), 'error'); return; }
    this.saving = true;
    try {
      const body = { title: this.form.title.trim(), content: this.form.content, folder_id: this.form.folder_id || null };
      if (this.editId) await api.put(`/events/${this.eventId}/notes/${this.editId}`, body);
      else await api.post(`/events/${this.eventId}/notes`, body);
      this.editing = false;
      await this.load();
      showToast(t('events.toast.noteSaved'), 'success');
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.saving = false; }
  }
  // Load a Markdown/text file straight into the content field. Pure client-side —
  // note content is already Markdown, so importing is just reading the file as text.
  private async importFromFile(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = ''; // allow re-picking the same file
    if (!file) return;
    const name = file.name.toLowerCase();
    if (!TEXT_IMPORT_EXTS.some((ext) => name.endsWith(ext))) {
      showToast(t('events.toast.importTypeError'), 'error');
      return;
    }
    if (file.size > MAX_IMPORT_BYTES) { showToast(t('events.toast.importTooLarge'), 'error'); return; }
    try {
      const text = await file.text();
      const title = this.form.title.trim() || file.name.replace(/\.[^.]+$/, '');
      this.form = { ...this.form, title, content: text };
      showToast(t('events.toast.fileLoadedIntoNote'), 'success');
    } catch {
      showToast(t('events.toast.couldNotReadFile'), 'error');
    }
  }

  private deleteNote(n: Note) {
    this.askConfirm({
      heading: t('events.notes.deleteNote.heading'),
      message: t('events.notes.deleteNote.message', { name: n.title }),
      confirmLabel: t('events.notes.deleteNote.confirm'),
    }, async () => {
      await api.del(`/events/${this.eventId}/notes/${n.id}`);
      await this.load();
    });
  }

  private folderName(id: string | null): string {
    return id ? (this.folders.find((f) => f.id === id)?.name ?? t('events.notes.folderFallback')) : t('events.notes.unfiled');
  }

  render() {
    if (this.loading) return html`<cadence-loading></cadence-loading>`;
    const hasUnfiled = this.notes.some((n) => !n.folder_id);
    return html`
      <div class="bar">
        <div class="chips">
          <span class="chip ${this.activeFolder === 'all' ? 'active' : ''}" @click=${() => (this.activeFolder = 'all')}>${t('events.notes.all', { count: this.notes.length })}</span>
          ${this.folders.map((f) => html`
            <span class="chip ${this.activeFolder === f.id ? 'active' : ''}" @click=${() => (this.activeFolder = f.id)}>
              ${f.name}
              ${this.canManage ? html`<span class="x" title=${t('events.notes.deleteFolderTitle')} @click=${(e: Event) => { e.stopPropagation(); this.deleteFolder(f); }}>×</span>` : ''}
            </span>`)}
          ${hasUnfiled ? html`<span class="chip ${this.activeFolder === 'unfiled' ? 'active' : ''}" @click=${() => (this.activeFolder = 'unfiled')}>${t('events.notes.unfiled')}</span>` : ''}
        </div>
        ${this.canManage ? html`
          <cadence-button variant="ghost" size="sm" @click=${() => this.openFolderModal(false)}>${t('events.notes.newFolder')}</cadence-button>
          <cadence-button size="sm" @click=${() => this.openNew()}>${t('events.notes.newNote')}</cadence-button>` : ''}
      </div>

      ${this.visibleNotes.length
        ? this.visibleNotes.map((n) => html`
          <details class="note" ?open=${this.openId === n.id} @toggle=${(e: Event) => { const el = e.target as HTMLDetailsElement; if (el.open) this.openId = n.id; else if (this.openId === n.id) this.openId = null; }}>
            <summary>
              <span class="title">${n.title}</span>
              <span class="meta">${this.folderName(n.folder_id)}${n.author_name ? ' · ' + n.author_name : ''}</span>
              ${this.canManage ? html`<span class="row-acts">
                <button class="iconbtn" title=${t('events.notes.editNoteTitle')} aria-label=${t('events.notes.editNoteTitle')}
                  @click=${(e: Event) => { e.preventDefault(); e.stopPropagation(); this.openEdit(n); }}>${icon('pencil', 16)}</button>
                <button class="iconbtn del" title=${t('events.notes.deleteNoteTitle')} aria-label=${t('events.notes.deleteNoteTitle')}
                  @click=${(e: Event) => { e.preventDefault(); e.stopPropagation(); this.deleteNote(n); }}>${icon('trash-2', 16)}</button>
              </span>` : ''}
            </summary>
            <div class="body">
              <cadence-markdown-renderer .markdown=${n.content ?? ''}></cadence-markdown-renderer>
            </div>
          </details>`)
        : html`<cadence-empty-state heading=${t('events.notes.empty.heading')} message=${this.canManage ? t('events.notes.empty.manage') : t('common.states.empty')}></cadence-empty-state>`}

      <cadence-modal ?open=${this.editing} heading=${this.editId ? t('events.notes.editNote') : t('events.notes.newNote')} @close=${() => (this.editing = false)}>
        <div class="efield">
          <cadence-input label=${t('events.notes.titleLabel')} .value=${this.form.title} @input=${(e: CustomEvent) => (this.form = { ...this.form, title: e.detail })}></cadence-input>
        </div>
        <div class="efield">
          <div class="efield-head">
            <label>${t('events.notes.folderLabel')}</label>
            <button type="button" class="import-btn" title=${t('events.notes.createFolderTitle')} @click=${() => this.openFolderModal(true)}>
              ${icon('folder-plus', 16)} ${t('events.notes.newFolder')}
            </button>
          </div>
          <cadence-select .value=${this.form.folder_id}
            .options=${[{ value: '', label: t('events.notes.unfiled') }, ...this.folders.map((f) => ({ value: f.id, label: f.name }))]}
            @change=${(e: CustomEvent) => (this.form = { ...this.form, folder_id: e.detail })}></cadence-select>
        </div>
        <div class="efield">
          <div class="efield-head">
            <label>${t('events.notes.contentLabel')}</label>
            <button type="button" class="import-btn" title=${t('events.notes.loadFileTitle')} @click=${() => this.importInput?.click()}>
              ${icon('upload', 16)} ${t('events.notes.loadFile')}
            </button>
          </div>
          <input type="file" accept=".md,.markdown,.mdown,.txt,text/markdown,text/plain" @change=${(e: Event) => this.importFromFile(e)} />
          <cadence-rich-text-editor .value=${this.form.content} @input=${(e: CustomEvent) => (this.form = { ...this.form, content: e.detail })}></cadence-rich-text-editor>
        </div>
        <div slot="footer">
          <cadence-button variant="ghost" @click=${() => (this.editing = false)}>${t('common.actions.cancel')}</cadence-button>
          <cadence-button ?loading=${this.saving} @click=${() => this.saveNote()}>${t('events.notes.saveNote')}</cadence-button>
        </div>
      </cadence-modal>

      <cadence-modal ?open=${this.folderModalOpen} heading=${t('events.notes.newFolder')} @close=${() => (this.folderModalOpen = false)}>
        <div class="efield">
          <cadence-input label=${t('events.notes.folderName')} .value=${this.newFolderName}
            @input=${(e: CustomEvent) => (this.newFolderName = e.detail as string)}
            @enter=${() => this.saveFolder()}></cadence-input>
        </div>
        <div slot="footer">
          <cadence-button variant="ghost" @click=${() => (this.folderModalOpen = false)}>${t('common.actions.cancel')}</cadence-button>
          <cadence-button ?loading=${this.folderSaving} @click=${() => this.saveFolder()}>${t('events.notes.createFolder')}</cadence-button>
        </div>
      </cadence-modal>

      <cadence-modal ?open=${this.confirmOpen} heading=${this.confirmConfig?.heading ?? ''} @close=${() => this.closeConfirm()}>
        <p style="margin:0">${this.confirmConfig?.message ?? ''}</p>
        <div slot="footer">
          <cadence-button variant="ghost" ?disabled=${this.confirmBusy} @click=${() => this.closeConfirm()}>${t('common.actions.cancel')}</cadence-button>
          <cadence-button variant="danger" ?loading=${this.confirmBusy} @click=${() => this.runConfirm()}>${this.confirmConfig?.confirmLabel ?? t('events.actions.confirm')}</cadence-button>
        </div>
      </cadence-modal>
    `;
  }
}
